import { z } from "zod";
const key = z.string().regex(/^[a-z][a-z0-9_]{0,79}$/u);
const referenceKey = z.string().regex(/^[a-z0-9][a-z0-9._-]{0,119}$/u);
const text = z.string().trim().min(1).max(1000);
const sha256 = z.string().regex(/^[0-9a-f]{64}$/u);
const exactNumber = z.string().max(200).regex(/^-?(?:0|[1-9]\d*)(?:\.\d+)?$/u);
const defaultMaterialitySchema = z.object({
    materiality: z.enum(["immaterial", "material"]),
    disclosure: text,
}).strict();
const parameterSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("enum"), key, label: text, description: text,
        options: z.array(z.object({ value: key, label: text }).strict()).min(1),
        default: z.object({ value: key }).extend(defaultMaterialitySchema.shape).strict().nullable(),
    }).strict(),
    z.object({
        kind: z.literal("boolean"), key, label: text, description: text,
        default: z.object({ value: z.boolean() }).extend(defaultMaterialitySchema.shape).strict().nullable(),
    }).strict(),
    z.object({
        kind: z.literal("number_range"), key, label: text, description: text,
        numberType: z.enum(["integer", "decimal"]),
        minimum: exactNumber,
        maximum: exactNumber,
        default: z.object({
            value: exactNumber,
        }).extend(defaultMaterialitySchema.shape).strict().nullable(),
    }).strict(),
    z.object({
        kind: z.literal("reference_member"), key, label: text, description: text,
        referenceSetKey: referenceKey,
        default: z.object({ value: key }).extend(defaultMaterialitySchema.shape).strict().nullable(),
    }).strict(),
]);
const selectionSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("enum_value"), parameterKey: key, value: key }).strict(),
    z.object({ kind: z.literal("boolean_value"), parameterKey: key, value: z.boolean() }).strict(),
    z.object({ kind: z.literal("reference_member"), parameterKey: key, value: key }).strict(),
]);
const combinationConstraintSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("requires"), when: selectionSchema, then: selectionSchema }).strict(),
    z.object({ kind: z.literal("excludes"), left: selectionSchema, right: selectionSchema }).strict(),
    z.object({ kind: z.literal("at_least_one"), parameterKeys: z.array(key).min(2) }).strict(),
]);
export const capabilityFailureCodeSchema = z.enum([
    "ACCESS_DENIED",
    "AMBIGUOUS_REQUEST",
    "EVIDENCE_EXPIRED",
    "INVALID_COMBINATION",
    "MISSING_REQUIRED_PARAMETER",
    "REFERENCE_UNAVAILABLE",
    "SOURCE_INCOMPLETE",
    "UNSUPPORTED_INTENT",
]);
export const capabilitySourceSchema = z.object({
    schemaVersion: z.literal(1),
    capabilityKey: key,
    title: text,
    description: text,
    measure: z.object({
        key, label: text,
        valueType: z.enum(["decimal", "integer", "currency", "duration_ms", "opaque"]),
        aggregation: z.enum(["sum", "count", "count_distinct", "average", "minimum", "maximum", "unavailable"]),
        currencyReferenceSetKey: referenceKey.nullable(),
    }).strict(),
    grain: z.object({
        entityKey: key,
        timeRole: key.nullable(),
        dimensions: z.array(key),
    }).strict(),
    parameters: z.array(parameterSchema),
    combinationConstraints: z.array(combinationConstraintSchema),
    failureOutcomes: z.array(z.object({
        code: capabilityFailureCodeSchema,
        userExplanation: text,
    }).strict()).min(1),
    access: z.object({
        requiredCapabilities: z.array(key),
        allowedOrganisationRoles: z.array(z.enum(["org:admin", "org:member"])).min(1),
        dataClassification: z.enum(["internal", "confidential", "restricted"]),
    }).strict(),
    routing: z.object({
        differentiators: z.array(text).min(1),
        negativeDifferentiators: z.array(text),
        automaticRoutingEligible: z.boolean(),
    }).strict(),
    validationObligations: z.array(z.discriminatedUnion("kind", [
        z.object({ kind: z.literal("contrastive"), rivalPolicyKeys: z.array(key).min(1) }).strict(),
        z.object({ kind: z.literal("trusted_number"), referenceKey, tolerance: z.string().regex(/^0(?:\.\d+)?$/u) }).strict(),
        z.object({ kind: z.literal("negative_control"), referenceKey }).strict(),
    ])).min(1),
    referenceSetKeys: z.array(referenceKey),
    semanticPlanDigest: sha256,
}).strict().superRefine((source, context) => {
    if ((source.measure.valueType === "opaque") !== (source.measure.aggregation === "unavailable")) {
        context.addIssue({ code: "custom", path: ["measure"],
            message: "Opaque value type and unavailable aggregation must occur together." });
    }
});
